import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Star, Mail, Linkedin, Copy, Check, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";

const PLATFORM_META = {
  google: { label: "Google", hint: "Ask happy customers for a Google review. Paste your Google review link (Business Profile → Ask for reviews)." },
  facebook: { label: "Facebook", hint: "Ask happy customers to recommend you on your Facebook Page reviews tab." },
};

export default function ReviewsPage() {
  const [config, setConfig] = useState({ google_review_url: "", facebook_review_url: "" });
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState("google");
  const [draft, setDraft] = useState(null); // {message, url, platform, channel, prospect}
  const [drafting, setDrafting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [gbp, setGbp] = useState(null);
  const [gbpPost, setGbpPost] = useState("");
  const [gbpBusy, setGbpBusy] = useState(false);
  const [reply, setReply] = useState({ reviewer_name: "", rating: "", review_text: "" });
  const [replyOut, setReplyOut] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyCopied, setReplyCopied] = useState(false);

  const draftReply = async () => {
    if (reply.review_text.trim().length < 3) { toast.error("Paste the review text first"); return; }
    setReplyBusy(true); setReplyOut("");
    try {
      const { data } = await api.post("/reviews/reply-draft", {
        platform, reviewer_name: reply.reviewer_name,
        rating: reply.rating ? Number(reply.rating) : null, review_text: reply.review_text,
      });
      setReplyOut(data.reply);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft reply"); }
    setReplyBusy(false);
  };
  const copyReply = async () => {
    try { await navigator.clipboard.writeText(replyOut); setReplyCopied(true); setTimeout(() => setReplyCopied(false), 1500); }
    catch { toast.error("Copy failed"); }
  };

  const loadGbp = async () => { try { const { data } = await api.get("/reviews/gbp/status"); setGbp(data); } catch { /* ignore */ } };
  useEffect(() => {
    loadGbp();
    const g = new URLSearchParams(window.location.search).get("gbp");
    if (g === "connected") { toast.success("Google Business Profile connected 🎉"); loadGbp(); }
    else if (g === "error") toast.error("Google connection failed — check API access & try again.");
    if (g) window.history.replaceState({}, "", "/app/reviews");
  }, []);

  const gbpConnect = async () => {
    setGbpBusy(true);
    try { const { data } = await api.get("/reviews/gbp/oauth/start"); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Google isn't configured yet"); setGbpBusy(false); }
  };
  const gbpPublish = async () => {
    if (gbpPost.trim().length < 5) { toast.error("Write a short update first"); return; }
    setGbpBusy(true);
    try { await api.post("/reviews/gbp/post", { summary: gbpPost, cta_url: config.google_review_url }); toast.success("Posted to Google Business Profile"); setGbpPost(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    setGbpBusy(false);
  };
  const gbpDisconnect = async () => {
    setGbpBusy(true);
    try { await api.post("/reviews/gbp/disconnect"); toast.success("Google disconnected"); await loadGbp(); }
    catch { toast.error("Could not disconnect"); }
    setGbpBusy(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cfg }, { data: cand }] = await Promise.all([
        api.get("/reviews/config"),
        api.get("/reviews/candidates"),
      ]);
      setConfig(cfg);
      setCandidates(cand.candidates || []);
    } catch { toast.error("Could not load reviews data"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveLinks = async () => {
    try {
      const { data } = await api.post("/reviews/config", config);
      setConfig(data);
      toast.success("Review links saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const openDraft = async (prospect, channel) => {
    setDrafting(true);
    setDraft({ prospect, channel, platform, message: "", url: "" });
    try {
      const { data } = await api.post("/reviews/draft", { prospect_id: prospect.id, platform, channel });
      setDraft({ prospect, channel, platform, message: data.message, url: data.url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft message");
      setDraft(null);
    }
    setDrafting(false);
  };

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(draft.message); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed — select the text manually"); }
  };

  const markAsked = async () => {
    try {
      await api.post("/reviews/mark-asked", { prospect_id: draft.prospect.id, platform: draft.platform, channel: draft.channel });
      toast.success(`Marked as asked — ${draft.prospect.full_name || draft.prospect.company}`);
      setDraft(null);
      load();
    } catch { toast.error("Could not record"); }
  };

  const linkKey = platform === "google" ? "google_review_url" : "facebook_review_url";
  const currentLink = config[linkKey] || "";

  const askedFor = (c, plat) => (c.asked || []).some((a) => a.platform === plat);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="reviews-page">
        <div className="flex items-center gap-3">
          <Star className="w-6 h-6 text-amber-accent" />
          <div>
            <h1 className="text-2xl font-semibold">Reviews & Reputation</h1>
            <p className="text-sm text-muted-foreground">Run separate campaigns to win more Google and Facebook reviews from your happy, won-deal customers.</p>
          </div>
        </div>

        <Tabs value={platform} onValueChange={setPlatform}>
          <TabsList data-testid="reviews-platform-tabs">
            <TabsTrigger value="google" data-testid="reviews-tab-google">Google Reviews</TabsTrigger>
            <TabsTrigger value="facebook" data-testid="reviews-tab-facebook">Facebook Reviews</TabsTrigger>
          </TabsList>

          {["google", "facebook"].map((plat) => (
            <TabsContent key={plat} value={plat} className="space-y-4 pt-4">
              <div className="rounded-md border border-border p-4 space-y-2">
                <div className="text-sm font-medium">{PLATFORM_META[plat].label} review link</div>
                <p className="text-[0.72rem] text-muted-foreground">{PLATFORM_META[plat].hint}</p>
                <div className="flex gap-2 items-center">
                  <Input
                    data-testid={`reviews-link-input-${plat}`}
                    value={plat === "google" ? config.google_review_url : config.facebook_review_url}
                    onChange={(e) => setConfig((c) => ({ ...c, [plat === "google" ? "google_review_url" : "facebook_review_url"]: e.target.value }))}
                    placeholder={plat === "google" ? "https://g.page/r/…/review" : "https://www.facebook.com/yourpage/reviews"}
                  />
                  <Button variant="outline" className="shrink-0" onClick={saveLinks} data-testid={`reviews-link-save-${plat}`}>
                    <Save className="w-4 h-4 mr-2" />Save
                  </Button>
                  {currentLink && (
                    <a href={currentLink} target="_blank" rel="noreferrer" className="shrink-0 text-signal-500" title="Open link" data-testid={`reviews-link-open-${plat}`}>
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>

              <div className="rounded-md border border-border">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <span className="text-sm font-medium">Happy customers to ask ({candidates.length})</span>
                  <span className="text-[0.7rem] text-muted-foreground">Won-deal contacts from your pipeline</span>
                </div>
                {loading ? (
                  <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                ) : candidates.length === 0 ? (
                  <div className="p-6 text-sm text-muted-foreground" data-testid="reviews-empty">
                    No won-deal customers yet. Move deals to the <strong>Won</strong> stage in your Pipeline, then ask them here.
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {candidates.map((c) => (
                      <div key={c.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`reviews-candidate-${c.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{c.full_name || c.company || "Customer"}</div>
                          <div className="text-[0.72rem] text-muted-foreground truncate">
                            {c.company}{c.email ? ` · ${c.email}` : ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {askedFor(c, plat) && (
                            <span className="text-[0.65rem] px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-500" data-testid={`reviews-asked-badge-${c.id}`}>Asked ✓</span>
                          )}
                          <Button size="sm" variant="outline" disabled={!c.email} onClick={() => openDraft(c, "email")} data-testid={`reviews-draft-email-${c.id}`}>
                            <Mail className="w-4 h-4 mr-1.5" />Email
                          </Button>
                          <Button size="sm" variant="outline" disabled={!c.linkedin_url} onClick={() => openDraft(c, "linkedin")} data-testid={`reviews-draft-linkedin-${c.id}`}>
                            <Linkedin className="w-4 h-4 mr-1.5" />LinkedIn
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-md border border-border p-4 space-y-3" data-testid="review-reply-drafter">
                <div>
                  <div className="text-sm font-medium">Respond to a review (AI reply drafter)</div>
                  <p className="text-[0.72rem] text-muted-foreground">Paste any {PLATFORM_META[plat].label} review — get a professional, on-brand public reply to copy. You approve & post it yourself.</p>
                </div>
                <div className="flex gap-2">
                  <Input value={reply.reviewer_name} onChange={(e) => setReply((r) => ({ ...r, reviewer_name: e.target.value }))} placeholder="Reviewer name (optional)" data-testid="reply-name-input" />
                  <select value={reply.rating} onChange={(e) => setReply((r) => ({ ...r, rating: e.target.value }))} data-testid="reply-rating-select"
                    className="flex h-9 rounded-sm border border-border bg-background px-2 text-sm w-40">
                    <option value="">Rating…</option>
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} ★</option>)}
                  </select>
                </div>
                <Textarea rows={3} value={reply.review_text} onChange={(e) => setReply((r) => ({ ...r, review_text: e.target.value }))} placeholder="Paste the review text here…" data-testid="reply-review-input" />
                <Button size="sm" className="rounded-sm" disabled={replyBusy} onClick={draftReply} data-testid="reply-draft-btn">
                  <Star className="w-4 h-4 mr-2" />{replyBusy ? "Drafting reply…" : "Draft reply with AI"}
                </Button>
                {replyOut && (
                  <div className="space-y-2 pt-1">
                    <Textarea rows={4} value={replyOut} onChange={(e) => setReplyOut(e.target.value)} data-testid="reply-output" />
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={copyReply} data-testid="reply-copy-btn">
                      {replyCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}{replyCopied ? "Copied" : "Copy reply"}
                    </Button>
                  </div>
                )}
              </div>

              {plat === "google" && (
                <div className="rounded-md border border-border p-4 space-y-3" data-testid="gbp-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">Google Business Profile — posts & review replies</div>
                      <p className="text-[0.72rem] text-muted-foreground">Post updates to Google (like Facebook) and reply to Google reviews from here.</p>
                    </div>
                    {gbp && (
                      <span className={`text-[0.65rem] px-2 py-0.5 rounded-sm ${gbp.connected ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-accent/10 text-amber-accent"}`} data-testid="gbp-status-badge">
                        {gbp.connected ? `Connected: ${gbp.location_title || "Google"}` : gbp.app_configured ? "Not connected" : "Awaiting Google approval"}
                      </span>
                    )}
                  </div>

                  {!gbp?.app_configured ? (
                    <div className="text-[0.72rem] text-muted-foreground bg-muted/40 rounded-sm p-3" data-testid="gbp-awaiting">
                      Google posting/reviews go live once Google approves API access (~14 days) and the Google Cloud keys are added.
                      Meanwhile, the Google <strong>review-request</strong> campaign above works today.
                    </div>
                  ) : !gbp?.connected ? (
                    <Button size="sm" className="rounded-sm" disabled={gbpBusy} onClick={gbpConnect} data-testid="gbp-connect-btn">Connect Google Business Profile</Button>
                  ) : (
                    <div className="space-y-3">
                      <Textarea rows={3} value={gbpPost} onChange={(e) => setGbpPost(e.target.value)} placeholder="Write a short update to post on Google (e.g. new Koper–Budapest lane, holiday cut-off dates)…" data-testid="gbp-post-input" />
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-sm" disabled={gbpBusy} onClick={gbpPublish} data-testid="gbp-publish-btn">Publish to Google</Button>
                        <Button size="sm" variant="outline" className="rounded-sm text-destructive" disabled={gbpBusy} onClick={gbpDisconnect} data-testid="gbp-disconnect-btn">Disconnect</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-w-lg" data-testid="reviews-draft-dialog">
          <DialogHeader>
            <DialogTitle>
              {PLATFORM_META[platform].label} review request — {draft?.channel === "email" ? "Email" : "LinkedIn DM"}
            </DialogTitle>
          </DialogHeader>
          {drafting ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Drafting a personalized message…</div>
          ) : (
            <div className="space-y-3">
              <Textarea rows={10} value={draft?.message || ""} onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))} data-testid="reviews-draft-textarea" />
              <p className="text-[0.7rem] text-muted-foreground">Review the message, tweak if needed, then copy & send it, and mark it as asked.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={copyMessage} disabled={drafting} data-testid="reviews-copy-btn">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}{copied ? "Copied" : "Copy message"}
            </Button>
            <Button onClick={markAsked} disabled={drafting} data-testid="reviews-mark-asked-btn">Mark as asked</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
